import { alphaBounds } from '../../shared/placement-check'
import type { Bounds } from '../../shared/placement-check'

const SAMPLE = 128
const LOAD_TIMEOUT_MS = 3000

/**
 * The alpha box of an image, measured by drawing it small and reading the pixels. Returns
 * undefined — never a wrong box — when the environment has no canvas (jsdom) or the draw is
 * tainted, so a missing measurement degrades to no warning rather than a false one.
 */
export async function measureBounds(
  url: string,
  timeoutMs: number = LOAD_TIMEOUT_MS,
): Promise<Bounds | undefined> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    return undefined
  }

  const canvas = document.createElement('canvas')
  const context = canvas.getContext?.('2d')
  if (!context) {
    return undefined
  }
  canvas.width = SAMPLE
  canvas.height = SAMPLE

  try {
    const image = await load(url, timeoutMs)
    context.clearRect(0, 0, SAMPLE, SAMPLE)
    context.drawImage(image, 0, 0, SAMPLE, SAMPLE)
    const { data } = context.getImageData(0, 0, SAMPLE, SAMPLE)

    return alphaBounds(data, SAMPLE, SAMPLE)
  } catch {
    return undefined
  }
}

// An image that never fires load or error would leave this promise pending for the life of the
// page, so the wait is bounded.
function load(url: string, timeoutMs: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const timer = setTimeout(
      () => reject(new Error(`timed out loading ${url}`)),
      timeoutMs,
    )
    const settle = (fn: () => void) => {
      clearTimeout(timer)
      fn()
    }
    image.crossOrigin = 'anonymous'
    image.onload = () => settle(() => resolve(image))
    image.onerror = () => settle(() => reject(new Error(`could not load ${url}`)))
    image.src = url
  })
}
